import { AlertTriangle, ImageIcon } from 'lucide-react'
import { useState } from 'react'
import type { Lang } from '../../../lib/i18n'
import type { OrderItemRow } from '../../../lib/orders-api'
import { Badge } from '../../../components/ui/badge'
import { Ltr } from '../Ltr'
import { formatMoney } from '../format'
import { ot } from '../orders-i18n'
import { SectionCard } from './SectionCard'

const ALLERGEN_PATTERN = /\b(nut|peanut|allerg\w*|gluten|dairy|lactose|egg|soy|shellfish|sub(?:stitut\w*|-\w+)?|erythritol|sugar[- ]?free|sweetener)\b/i

function flattenCustomizations(json: unknown): string[] {
  if (json == null) return []
  if (Array.isArray(json)) return json.map((v) => (typeof v === 'string' ? v : JSON.stringify(v)))
  if (typeof json === 'object') {
    return Object.entries(json as Record<string, unknown>).map(([k, v]) => {
      if (Array.isArray(v)) return `${k}: ${v.join(', ')}`
      if (typeof v === 'object' && v !== null) return `${k}: ${JSON.stringify(v)}`
      return `${k}: ${v}`
    })
  }
  return [String(json)]
}

// ux2 (d): the first usable image of the snapshot, or null. The snapshot is stored JSON, so the
// shape is checked here rather than trusted from the type.
function firstImageUrl(imageUrls: unknown): string | null {
  if (!Array.isArray(imageUrls)) return null
  const url = imageUrls.find((u): u is string => typeof u === 'string' && u.trim() !== '')
  return url ?? null
}

// ux2 (d): fixed size so the row never shifts when the image lands; a neutral box when there is
// no image or it fails to load.
function ItemThumb({ src, alt }: { src: string | null; alt: string }) {
  const [failed, setFailed] = useState(false)
  return (
    <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted text-muted-foreground sm:h-14 sm:w-14">
      {src && !failed ? (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          width={56}
          height={56}
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <ImageIcon className="h-5 w-5" aria-hidden data-testid="item-thumb-placeholder" />
      )}
    </div>
  )
}

function ItemRow({ item, lang, currency }: { item: OrderItemRow; lang: Lang; currency: string }) {
  const name = lang === 'ar' && item.productSnapshot.nameAr ? item.productSnapshot.nameAr : item.productSnapshot.nameEn ?? '—'
  const variantName = item.variantSnapshot
    ? (lang === 'ar' && item.variantSnapshot.nameAr ? item.variantSnapshot.nameAr : item.variantSnapshot.nameEn)
    : null
  const customizations = flattenCustomizations(item.customizationsJson)
  const allergens = item.productSnapshot.allergens
  const altName = (lang === 'ar' && item.productSnapshot.nameAr ? item.productSnapshot.nameAr : item.productSnapshot.nameEn) ?? ''

  return (
    <div className="flex gap-4 py-4">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-navy text-xl font-bold text-white sm:h-14 sm:w-14 sm:text-2xl">
        <Ltr>{item.quantity}×</Ltr>
      </div>
      <ItemThumb src={firstImageUrl(item.productSnapshot.imageUrls)} alt={altName} />
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
          <bdi className="text-lg font-semibold text-foreground">{name}</bdi>
          <Ltr className="whitespace-nowrap text-sm font-semibold text-foreground">
            {formatMoney(item.lineTotal, currency)}
          </Ltr>
        </div>
        {item.productSnapshot.unmapped === true && (
          <Badge variant="destructive" className="gap-1">
            <AlertTriangle className="h-3 w-3" /> {ot(lang, 'unmappedItem')}
          </Badge>
        )}
        {variantName && <bdi className="block text-sm text-muted-foreground">{variantName}</bdi>}
        <Ltr className="block text-xs text-muted-foreground">
          {formatMoney(item.unitPrice, currency)} {ot(lang, 'each')}
        </Ltr>

        {allergens && allergens.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {allergens.map((a) => (
              <Badge key={a} variant="destructive" className="gap-1">
                <AlertTriangle className="h-3 w-3" /> {a}
              </Badge>
            ))}
          </div>
        )}

        {customizations.length > 0 && (
          <ul className="ms-2 space-y-1 border-s-2 border-border ps-3">
            {customizations.map((c, i) => {
              const flagged = ALLERGEN_PATTERN.test(c)
              return (
                <li
                  key={i}
                  className={
                    flagged
                      ? 'flex items-start gap-1.5 rounded-md bg-destructive/10 px-2 py-1 text-sm font-medium text-destructive'
                      : 'rounded-md bg-secondary/60 px-2 py-1 text-sm text-foreground'
                  }
                >
                  {flagged && <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
                  <bdi>{c}</bdi>
                </li>
              )
            })}
          </ul>
        )}

        {item.isPromoReward && <Badge variant="sage">{ot(lang, 'promoTag')}</Badge>}
      </div>
    </div>
  )
}

interface Props {
  items: OrderItemRow[]
  lang: Lang
  currency: string
}

export function ItemsSection({ items, lang, currency }: Props) {
  return (
    <SectionCard title={ot(lang, 'itemsTitle')} lang={lang} className="border-2 border-navy/15" contentClassName="pt-0">
      {items.length === 0 ? (
        <p className="py-4 text-sm text-muted-foreground">{ot(lang, 'noItems')}</p>
      ) : (
        <div className="divide-y">
          {items.map((item) => (
            <ItemRow key={item.id} item={item} lang={lang} currency={currency} />
          ))}
        </div>
      )}
    </SectionCard>
  )
}
